import Mustache from 'mustache';
import i18n from '../utils/i18n.js';
import toast from '../utils/toast.js';
import { translateDayText } from '../utils/utils.js';
import { trackBeginCheckout, trackCartAbandoned, trackCheckoutCanceled } from '../utils/analytics.js';

export function initCart(cart, domElements, callbacks) {
    const {
        cartSidebar,
        cartToggleButton,
        cartBody,
        cartBuyButton,
        cartBadge,
        sidebarOverlay
    } = domElements;

    const {
        onCheckout,
        onOpenRegister,
        checkOtherSidebars,
        account
    } = callbacks || {};

    if (!account) {
        console.error('Account is required for cart checkout');
    }

    const cartCheckoutSection = document.getElementById('cartCheckoutSection');
    const cartVerificationSection = document.getElementById('cartVerificationSection');
    const cartEmail = document.getElementById('cartEmail');
    const cartCode = document.getElementById('cartCode');
    const cartEmailGroup = document.getElementById('cartEmailGroup');
    const cartCodeGroup = document.getElementById('cartCodeGroup');
    const cartSendCodeButton = document.getElementById('cartSendCodeButton');
    const cartVerifyCodeButton = document.getElementById('cartVerifyCodeButton');

    let cartEmailSent = false;
    let checkoutEmail = '';

    function renderCart() {
        const cartItems = cart.getCart();
        const template = document.getElementById('template-cart-item');
        const templateHtml = template.innerHTML;

        if (cartItems.length === 0) {
            cartBody.innerHTML = `<div class="cart-empty"><p>${i18n.t('cartEmpty')}</p></div>`;
            cartBuyButton.disabled = true;
            cartBadge.classList.add('hidden');
            updateCartTotal();
            return;
        }

        cartBody.innerHTML = '';
        cartItems.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const subtotal = (price * item.quantity).toFixed(2);
            const { speed, hasSpeed, ...itemWithoutSpeed } = item;
            const data = {
                ...itemWithoutSpeed,
                name: translateDayText(item.name),
                durationUnit: translateDayText(item.durationUnit, item.duration),
                price: price.toFixed(2),
                subtotal: subtotal
            };

            const html = Mustache.render(templateHtml, data);
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const cartItem = tempDiv.firstElementChild;

            cartItem.querySelector('.cart-item-remove').addEventListener('click', () => {
                cart.removeFromCart(item.packageCode, 'packageCode');
                renderCart();
            });

            cartItem.querySelectorAll('.quantity-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const action = e.target.dataset.action;
                    const currentQuantity = item.quantity;
                    if (action === 'increase') {
                        cart.updateCartQuantity(item.packageCode, currentQuantity + 1, 'packageCode');
                    } else if (action === 'decrease') {
                        cart.updateCartQuantity(item.packageCode, currentQuantity - 1, 'packageCode');
                    }
                    renderCart();
                });
            });

            cartBody.appendChild(cartItem);
        });

        i18n.updatePage();

        const totalItems = cart.getTotalItems();
        if (totalItems > 0) {
            cartBadge.textContent = totalItems;
            cartBadge.classList.remove('hidden');
        } else {
            cartBadge.classList.add('hidden');
        }

        cartBuyButton.disabled = cartItems.length === 0;
        updateCartTotal();
    }

    function updateCartTotal() {
        const cartItems = cart.getCart();
        const total = cart.getCartTotal('price').toFixed(2);
        const totalElement = document.querySelector('.cart-total-amount');
        if (totalElement) {
            totalElement.textContent = `$${total}`;
        }

        // Calculate total processing fee if there are charges
        const totalCharge = cartItems.reduce((sum, item) => {
            // charge can be a string (from preparePackageDetailsData) or number
            let charge = 0;
            if (item.charge !== undefined && item.charge !== null) {
                charge = typeof item.charge === 'string' ? parseFloat(item.charge) : item.charge;
            }
            return sum + (charge * item.quantity);
        }, 0);

        const processingFeeSection = document.getElementById('cartProcessingFee');
        const feeTotalElement = document.querySelector('.cart-fee-total');

        if (totalCharge > 0 && processingFeeSection && feeTotalElement) {
            feeTotalElement.textContent = `$${totalCharge.toFixed(2)}`;
            processingFeeSection.classList.remove('hidden');
        } else if (processingFeeSection) {
            processingFeeSection.classList.add('hidden');
        }
    }

    function resetCartVerification() {
        cartEmailSent = false;
        checkoutEmail = '';
        cartCheckoutSection.classList.remove('hidden');
        cartVerificationSection.classList.add('hidden');
        cartEmailGroup.classList.remove('hidden');
        cartCodeGroup.classList.add('hidden');
        cartSendCodeButton.classList.remove('hidden');
        cartVerifyCodeButton.classList.add('hidden');
        cartEmail.value = '';
        cartCode.value = '';
        cartCode.removeAttribute('required');
    }

    function openCart() {
        cartSidebar.classList.remove('hidden');
        sidebarOverlay.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        resetCartVerification();
    }

    function closeCart() {
        // Track cart abandonment if cart has items
        const cartItems = cart.getCart();
        if (cartItems.length > 0) {
            trackCartAbandoned(cartItems);
        }
        
        cartSidebar.classList.add('hidden');
        if (checkOtherSidebars) {
            const hasOpenSidebar = checkOtherSidebars();
            if (!hasOpenSidebar) {
                sidebarOverlay.classList.add('hidden');
                document.body.style.overflow = '';
            }
        } else {
            sidebarOverlay.classList.add('hidden');
            document.body.style.overflow = '';
        }
    }

    async function handleSendCode() {
        const email = cartEmail.value.trim();
        if (!email) {
            toast.error(i18n.t('emailRequired'));
            return;
        }

        try {
            await account.requestCode(email);
            checkoutEmail = email;
            cartEmailSent = true;
            cartEmailGroup.classList.add('hidden');
            cartCodeGroup.classList.remove('hidden');
            cartCode.setAttribute('required', 'required');
            cartSendCodeButton.classList.add('hidden');
            cartVerifyCodeButton.classList.remove('hidden');
            toast.success(i18n.t('codeSent'));
        } catch (error) {
            toast.error(error.message || i18n.t('codeSendFailed'));
        }
    }

    async function handleVerifyCode() {
        const code = cartCode.value.trim();
        if (!code || code.length !== 6) {
            toast.error(i18n.t('invalidCode'));
            return;
        }

        try {
            await account.verifyCodeForCheckout(checkoutEmail, code);
            // Establecer el idioma del usuario si está definido
            const user = account.getCurrentUser();
            if (user && user.language) {
                i18n.setLanguage(user.language);
                if (window.updateLanguageButtons) {
                    window.updateLanguageButtons();
                }
            }
            toast.success(i18n.t('codeVerified'));
            
            const cartItems = cart.getCart();
            if (onCheckout) {
                try {
                    await onCheckout(cartItems);
                } catch (error) {
                    console.error('Error creating checkout session:', error);
                    toast.error(error.message || i18n.t('errorProcessingPayment'));
                }
            }
        } catch (error) {
            toast.error(error.message || i18n.t('codeVerificationFailed'));
        }
    }

    function setupEventListeners() {
        cartToggleButton.addEventListener('click', () => {
            openCart();
        });

        cartBuyButton.addEventListener('click', () => {
            const cartItems = cart.getCart();
            if (cartItems.length === 0) return;

            trackBeginCheckout(cartItems);

            if (account && account.isAuthenticated && account.isAuthenticated()) {
                if (onCheckout) {
                    onCheckout(cartItems).catch(error => {
                        console.error('Error creating checkout session:', error);
                        toast.error(error.message || i18n.t('errorProcessingPayment'));
                    });
                }
            } else if (account) {
                cartCheckoutSection.classList.add('hidden');
                cartVerificationSection.classList.remove('hidden');
            } else {
                toast.error('Account service is not available');
            }
        });

        if (cartSendCodeButton) {
            cartSendCodeButton.addEventListener('click', async (e) => {
                e.preventDefault();
                await handleSendCode();
            });
        }

        if (cartVerifyCodeButton) {
            cartVerifyCodeButton.addEventListener('click', async (e) => {
                e.preventDefault();
                await handleVerifyCode();
            });
        }

        if (cartEmail) {
            cartEmail.addEventListener('keypress', async (e) => {
                if (e.key === 'Enter' && !cartEmailSent) {
                    e.preventDefault();
                    await handleSendCode();
                }
            });
        }

        if (cartCode) {
            cartCode.addEventListener('keypress', async (e) => {
                if (e.key === 'Enter' && cartEmailSent) {
                    e.preventDefault();
                    await handleVerifyCode();
                }
            });
        }
    }

    setupEventListeners();
    renderCart();

    return {
        renderCart,
        updateCartTotal,
        openCart,
        closeCart
    };
}

